'use client'

import { useState, useEffect } from 'react'
import { Gift, X, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useRouter, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'

const ANNOUNCEMENT_KEY = 'heyboss_holiday_gift_card_2024'
const EXPIRY_DATE = new Date('2026-01-01T00:00:00Z')

/**
 * Holiday Gift Card Announcement Banner
 * Shows once per user, dismissable, auto-expires Jan 1, 2026
 * Only shows on homepage (/) for logged-in users
 */
export function GiftCardAnnouncement() {
  // Temporarily hidden
  return null

  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations('gift-card.announcement')
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    // Only show on homepage
    if (pathname !== '/') {
      return
    }

    // Check if announcement should be shown
    const now = new Date()

    // Don't show if past expiry date
    if (now >= EXPIRY_DATE) {
      return
    }

    // Don't show if user has dismissed it
    const dismissed = localStorage.getItem(ANNOUNCEMENT_KEY)
    if (dismissed === 'true') {
      return
    }

    // Show announcement after a short delay (less annoying)
    const timer = setTimeout(() => {
      setIsVisible(true)
      setTimeout(() => {
        setIsAnimating(true)
      }, 100)
    }, 2000) // Wait 2 seconds before showing

    return () => {
      clearTimeout(timer)
    }
  }, [pathname])

  const handleDismiss = () => {
    setIsAnimating(false)
    setTimeout(() => {
      setIsVisible(false)
      localStorage.setItem(ANNOUNCEMENT_KEY, 'true')
    }, 300)
  }

  const handleExplore = () => {
    localStorage.setItem(ANNOUNCEMENT_KEY, 'true')
    router.push('/gift-card')
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop - subtle */}
      <div
        className={cn(
          'absolute inset-0 bg-black/20 backdrop-blur-[2px] transition-opacity duration-300 pointer-events-auto',
          isAnimating ? 'opacity-100' : 'opacity-0',
        )}
        onClick={handleDismiss}
      />

      {/* Announcement Card */}
      <div
        className={cn(
          'fixed bottom-6 right-6 w-full max-w-md pointer-events-auto',
          'transform transition-all duration-300 ease-out',
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        )}
      >
        <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black border border-gold/30 rounded-xl shadow-[0_8px_32px_rgba(255,215,0,0.2)] overflow-hidden">
          {/* Sparkle Effect */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-gold/10 to-transparent rounded-full blur-3xl" />

          {/* Close Button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="relative p-6">
            {/* Icon */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-gold" />
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gold animate-pulse" />
                <span className="text-[11px] bg-red-500/90 text-white px-2 py-1 rounded-full font-bold">
                  🎄 {t('new_for_holidays')}
                </span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-white mb-2">{t('title')}</h3>

            {/* Description */}
            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              {t('description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleExplore}
                className="flex-1 bg-gold-gradient text-black hover:opacity-90 font-bold shadow-[0_0_20px_-5px_rgba(255,215,0,0.4)] border-0"
              >
                <Gift className="w-4 h-4 mr-2" />
                {t('explore_button')}
              </Button>
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="bg-white/5 text-white hover:bg-white/10 border-white/20"
              >
                {t('maybe_later')}
              </Button>
            </div>

            {/* Footer Note */}
            <p className="text-[10px] text-white/40 mt-3 text-center">
              {t('available_until')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
